import {
  useEffect,
  useState,
  type DragEvent,
  type FormEvent,
  type MutableRefObject
} from "react";
import {
  assetViewSchema,
  type AssetKind,
  type AssetListQuery,
  type AssetView
} from "@seedance/contracts";
import type { ApiClient } from "../../api/client.js";
import { canvasCopy } from "../../copy/zh-CN.js";

export type AssetPanelView = AssetListQuery["view"];
export type AssetPanelStatus = "idle" | "loading" | "ready" | "error";

export interface AssetPanelState {
  view: AssetPanelView;
  kind: AssetKind | undefined;
  query: string;
  assets: AssetView[];
  nextCursor: string | undefined;
  status: AssetPanelStatus;
  error: string | undefined;
}

export interface AssetPanelController {
  getState(): AssetPanelState;
  subscribe(listener: (state: AssetPanelState) => void): () => void;
  setView(view: AssetPanelView): void;
  setKind(kind: AssetKind | undefined): void;
  setQuery(query: string): void;
  load(): Promise<void>;
  loadMore(): Promise<void>;
  retry(): Promise<void>;
  trash(assetId: string): Promise<void>;
  restore(assetId: string): Promise<void>;
}

type AssetPanelClient = Pick<
  ApiClient,
  "listAssets" | "trashAsset" | "restoreAsset"
>;

function messageFor(_error: unknown): string {
  return canvasCopy.assetsLoadFailed;
}

export function createAssetPanelController({
  client,
  onAssets
}: {
  client: AssetPanelClient;
  onAssets?(assets: readonly AssetView[]): void;
}): AssetPanelController {
  let state: AssetPanelState = {
    view: "library",
    kind: undefined,
    query: "",
    assets: [],
    nextCursor: undefined,
    status: "idle",
    error: undefined
  };
  const listeners = new Set<(value: AssetPanelState) => void>();
  let requestGeneration = 0;
  let paginationRequest:
    | { generation: number; cursor: string; promise: Promise<void> }
    | undefined;
  const snapshot = (): AssetPanelState => ({ ...state, assets: [...state.assets] });
  const notify = () => {
    const value = snapshot();
    listeners.forEach((listener) => listener(value));
  };
  const update = (change: Partial<AssetPanelState>) => {
    state = { ...state, ...change };
    notify();
  };
  const requestQuery = (cursor?: string): AssetListQuery => {
    const query = state.query.trim();
    return {
      view: state.view,
      ...(state.kind ? { kind: state.kind } : {}),
      ...(query ? { q: query } : {}),
      ...(cursor ? { cursor } : {}),
      limit: 24
    };
  };
  const fetchPage = async ({
    append,
    generation,
    query
  }: {
    append: boolean;
    generation: number;
    query: AssetListQuery;
  }) => {
    update({ status: "loading", error: undefined });
    try {
      const response = await client.listAssets(query);
      if (generation !== requestGeneration) return;
      const assets = append ? [...state.assets, ...response.assets] : response.assets;
      state = {
        ...state,
        assets,
        status: "ready",
        error: undefined,
        ...(response.nextCursor
          ? { nextCursor: response.nextCursor }
          : { nextCursor: undefined })
      };
      onAssets?.(response.assets);
      notify();
    } catch (error) {
      if (generation !== requestGeneration) return;
      update({ status: "error", error: messageFor(error) });
    }
  };

  const load = () => {
    const generation = ++requestGeneration;
    return fetchPage({ append: false, generation, query: requestQuery() });
  };

  const loadMore = () => {
    const cursor = state.nextCursor;
    if (!cursor) return Promise.resolve();
    const generation = requestGeneration;
    if (
      paginationRequest?.generation === generation &&
      paginationRequest.cursor === cursor
    ) {
      return paginationRequest.promise;
    }
    const promise = fetchPage({
      append: true,
      generation,
      query: requestQuery(cursor)
    }).finally(() => {
      if (paginationRequest?.promise === promise) paginationRequest = undefined;
    });
    paginationRequest = { generation, cursor, promise };
    return promise;
  };

  return {
    getState: snapshot,
    subscribe(listener) {
      listeners.add(listener);
      listener(snapshot());
      return () => listeners.delete(listener);
    },
    setView(view) {
      requestGeneration += 1;
      state = {
        view,
        query: state.query,
        kind: state.kind,
        assets: [],
        nextCursor: undefined,
        status: "idle",
        error: undefined
      };
      notify();
    },
    setKind(kind) {
      requestGeneration += 1;
      state = {
        view: state.view,
        query: state.query,
        kind,
        assets: [],
        nextCursor: undefined,
        status: "idle",
        error: undefined
      };
      notify();
    },
    setQuery(query) {
      requestGeneration += 1;
      update({
        query,
        nextCursor: undefined,
        status: "idle",
        error: undefined
      });
    },
    load,
    loadMore,
    retry: load,
    async trash(assetId) {
      try {
        const asset = await client.trashAsset(assetId);
        onAssets?.([asset]);
        update({ assets: state.assets.filter(({ id }) => id !== assetId) });
      } catch (error) {
        update({ status: "error", error: messageFor(error) });
      }
    },
    async restore(assetId) {
      try {
        const asset = await client.restoreAsset(assetId);
        onAssets?.([asset]);
        update({ assets: state.assets.filter(({ id }) => id !== assetId) });
      } catch (error) {
        update({ status: "error", error: messageFor(error) });
      }
    }
  };
}

export const assetDragMimeType = "application/x-seedance-asset";

export function writeAssetDragData(dataTransfer: DataTransfer, asset: AssetView) {
  dataTransfer.effectAllowed = "copy";
  dataTransfer.setData(assetDragMimeType, JSON.stringify(asset));
}

export function readAssetDragData(dataTransfer: DataTransfer): AssetView | undefined {
  const serialized = dataTransfer.getData(assetDragMimeType);
  if (!serialized) return undefined;
  try {
    return assetViewSchema.parse(JSON.parse(serialized));
  } catch {
    return undefined;
  }
}

export function assignAssetSearchInputRef(
  ref: MutableRefObject<HTMLInputElement | null> | undefined,
  element: HTMLInputElement | null
): void {
  if (ref) ref.current = element;
}

const tabs: Array<{ label: string; view: AssetPanelView }> = [
  { label: "我的素材", view: "library" },
  { label: "历史素材", view: "history" },
  { label: "回收站", view: "trash" }
];

export function AssetPanel({
  controller,
  searchInputRef
}: {
  controller: AssetPanelController;
  searchInputRef?: MutableRefObject<HTMLInputElement | null>;
}) {
  const [state, setState] = useState(() => controller.getState());
  useEffect(() => controller.subscribe(setState), [controller]);
  useEffect(() => {
    if (controller.getState().status === "idle") void controller.load();
  }, [controller]);

  const selectView = (view: AssetPanelView) => {
    controller.setView(view);
    void controller.load();
  };
  const selectKind = (kind: string) => {
    controller.setKind(kind ? (kind as AssetKind) : undefined);
    void controller.load();
  };
  const search = (event: FormEvent) => {
    event.preventDefault();
    void controller.load();
  };
  const drag = (event: DragEvent, asset: AssetView) => {
    writeAssetDragData(event.dataTransfer, asset);
  };

  return (
    <aside className="studio-asset-panel" aria-label={canvasCopy.privateAssetLibrary}>
      <nav aria-label={canvasCopy.assetViews}>
        {tabs.map((tab) => (
          <button
            key={tab.view}
            type="button"
            aria-pressed={state.view === tab.view}
            onClick={() => selectView(tab.view)}
          >
            {tab.label}
          </button>
        ))}
      </nav>
      <form onSubmit={search}>
        <input
          ref={(element) => assignAssetSearchInputRef(searchInputRef, element)}
          aria-label={canvasCopy.searchAssets}
          placeholder={canvasCopy.searchAssets}
          value={state.query}
          onChange={(event) => controller.setQuery(event.currentTarget.value)}
        />
        <select
          aria-label={canvasCopy.assetKind}
          value={state.kind ?? ""}
          onChange={(event) => selectKind(event.currentTarget.value)}
        >
          <option value="">{canvasCopy.all}</option>
          <option value="image">{canvasCopy.images}</option>
          <option value="video">{canvasCopy.videos}</option>
          <option value="audio">{canvasCopy.audio}</option>
        </select>
        <button type="submit">{canvasCopy.search}</button>
      </form>
      {state.status === "error" ? (
        <p className="studio-asset-panel__notice" role="alert">
          {state.error}
          <button type="button" onClick={() => void controller.retry()}>{canvasCopy.retry}</button>
        </p>
      ) : null}
      <ul className="studio-asset-list" aria-busy={state.status === "loading"}>
        {state.assets.map((asset) => (
          <li
            key={asset.id}
            draggable={state.view !== "trash"}
            onDragStart={(event) => drag(event, asset)}
          >
            {asset.kind === "audio" ? (
              <span className="studio-asset-list__audio" aria-hidden="true">♫</span>
            ) : (
              <img
                src={`/api/assets/${encodeURIComponent(asset.id)}/thumbnail`}
                alt=""
                loading="lazy"
              />
            )}
            <span title={asset.displayName}>{asset.displayName}</span>
            {state.view === "trash" ? (
              <button type="button" onClick={() => void controller.restore(asset.id)}>{canvasCopy.restore}</button>
            ) : (
              <button type="button" onClick={() => void controller.trash(asset.id)}>{canvasCopy.trash}</button>
            )}
          </li>
        ))}
      </ul>
      {state.nextCursor ? (
        <button type="button" onClick={() => void controller.loadMore()}>{canvasCopy.loadMore}</button>
      ) : null}
    </aside>
  );
}
